import {
  CURRENT_OWNER_LABELS,
  WAITING_FOR_LABELS,
  COMMUNICATION_METHOD_LABELS,
  CUSTOMER_RESPONSE_LABELS,
  type AuthorizationCurrentOwner,
  type AuthorizationWaitingFor,
  type AuthorizationJourney,
  type AuthorizationJourneyHop,
  type AuthorizationCommunicationMethod,
  type CustomerResponseType,
} from '@/services/authorization-case.service';

/**
 * Authorization Radar — Stage 3 presentation layer.
 *
 * Authority: **D-112** · `AUTHORIZATION_RADAR_REQUEST_JOURNEY_DESIGN.md` v1.0
 * §3, §7, §8, §10, §16 · Stage 2 report (the journey contract).
 *
 * ── WHAT THIS FILE IS ───────────────────────────────────────────────────────
 * Pure mapping from the backend journey contract to what the radar draws. It is
 * deliberately separate from the component so the truthfulness rules can be
 * tested without rendering anything.
 *
 * ── WHAT IT NEVER DOES ──────────────────────────────────────────────────────
 * It never predicts a next stage, never infers a missing hop, never derives
 * ownership from `status`/`workflowStage`, and never treats the customer as an
 * owner. Every value it returns traces to a field the backend actually sent.
 *
 * There is no "expected route" anywhere in this file. The four owner domains are
 * a fixed spatial legend, NOT a sequence a case is assumed to follow.
 */

/**
 * The four governed owner domains, in their spatial display order.
 *
 * This ordering is the layout of the legend and nothing more. A case that goes
 * OPS -> ADMIN -> OPS -> ENGINEER never touches COMMERCIAL, and COMMERCIAL must
 * therefore render as uninvolved — not as a step that was skipped, and never as
 * a step still to come.
 *
 * CUSTOMER is deliberately absent: `AuthorizationCurrentOwner` has no CUSTOMER
 * member, and OD-R5 makes that unrepresentable rather than merely unwritten.
 */
export const RADAR_OWNER_NODES: readonly AuthorizationCurrentOwner[] = Object.freeze([
  'ENGINEER',
  'OPS',
  'COMMERCIAL',
  'ADMIN',
]);

export type RadarNodeState =
  /** The case's live `currentOwner`. Exactly one node holds this. */
  | 'ACTIVE'
  /** A persisted hop landed here at some point, but the case has moved on. */
  | 'VISITED'
  /** No persisted hop ever landed here. NOT "upcoming" — the radar has no future. */
  | 'UNINVOLVED';

export interface RadarNode {
  owner: AuthorizationCurrentOwner;
  label: string;
  state: RadarNodeState;
  /**
   * Populated only on the ACTIVE node, from the case's live `waitingFor`
   * (design §3.1: the active node is "annotated with `waitingFor`").
   */
  waitingForLabel: string | null;
  /** True when the active node is waiting on the customer rather than on itself. */
  waitingOnCustomer: boolean;
}

/**
 * Builds the four legend nodes.
 *
 * The ACTIVE node comes from the case's live `currentOwner` — **not** from the
 * last hop. Those can legitimately differ, and when they do it is the live row
 * that is authoritative (design §7, quoting the parent design: dashboards
 * "should read `currentOwner`/`waitingFor` directly and never re-derive").
 *
 * VISITED comes only from `toOwner` values that were actually persisted.
 */
export function buildRadarNodes(
  currentOwner: AuthorizationCurrentOwner,
  waitingFor: AuthorizationWaitingFor,
  hops: readonly AuthorizationJourneyHop[],
): RadarNode[] {
  const visited = new Set<AuthorizationCurrentOwner>();
  for (const hop of hops) {
    if (hop.toOwner) visited.add(hop.toOwner);
    if (hop.fromOwner) visited.add(hop.fromOwner);
  }

  return RADAR_OWNER_NODES.map((owner) => {
    const isActive = owner === currentOwner;
    return {
      owner,
      label: CURRENT_OWNER_LABELS[owner],
      state: isActive ? 'ACTIVE' : visited.has(owner) ? 'VISITED' : 'UNINVOLVED',
      waitingForLabel: isActive ? WAITING_FOR_LABELS[waitingFor] : null,
      waitingOnCustomer: isActive && waitingFor === 'CUSTOMER',
    };
  });
}

/**
 * Human labels for persisted actions. **Presentation only** — the underlying
 * action string is preserved on every rendered hop and is never rewritten in the
 * data layer.
 *
 * Note `ASSIGNED_TO_OPS` is NOT relabelled "Requested". That is the genesis
 * landing action Stage 2 actually persists, and renaming it would quietly
 * restate the record to match the design's conceptual diagram.
 */
const ACTION_LABELS: Record<string, string> = {
  REQUESTED: 'Requested',
  ASSIGNED_TO_OPS: 'Assigned to Operations',
  FORWARDED_TO_COMMERCIAL: 'Forwarded to Commercial',
  FORWARDED_TO_ADMIN: 'Forwarded to Admin',
  RETURNED_TO_OPS: 'Returned to Operations',
  RELEASED_TO_ENGINEER: 'Released to Engineer',
  RETURNED: 'Returned to Engineer',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  COMMENTED: 'Commented',
  EVIDENCE_ADDED: 'Evidence Added',
  EVIDENCE_REMOVED: 'Evidence Removed',
  // Stage 5G1 — the single durable override marker (D-122 Q1 = NO; a skipped
  // step is derived by the 5D comparison, never a durable action of its own).
  // "Deviation" is deliberately absent from the label: a sanctioned override is
  // not an error, and the radar already says so (5E copy, design §11.3).
  ROUTE_OVERRIDDEN: 'Route Overridden',
  // Stage 5G2 — [OWNER-RATIFIED] L-3 clause 3: a waiver is NOT an approval and
  // must never be rendered as one. The label names the act, not an outcome.
  CONSENT_WAIVED: 'Customer Consent Waived',
};

/**
 * Design §16: "unknown enum values must render as unknown, never be coerced."
 * An action this build has never heard of is shown verbatim rather than mapped
 * to the nearest familiar label.
 */
export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function methodLabel(method: string): string {
  return COMMUNICATION_METHOD_LABELS[method as AuthorizationCommunicationMethod] ?? method;
}

export function decisionLabel(decision: string): string {
  return CUSTOMER_RESPONSE_LABELS[decision as CustomerResponseType] ?? decision;
}

/**
 * The neutral fallback when a communication carries no linked contact.
 *
 * `customerContactId` is optional on the record-communication DTO, so a genuinely
 * unlinked interaction is normal. Reusing the governed `WAITING_FOR_LABELS`
 * value for CUSTOMER names the participant without inventing an identity — the
 * one thing §8 forbids here.
 */
export const NEUTRAL_CUSTOMER_LABEL = WAITING_FOR_LABELS.CUSTOMER;

export function customerParticipantLabel(hop: AuthorizationJourneyHop): string {
  return hop.customerContactName ?? NEUTRAL_CUSTOMER_LABEL;
}

/** A hop's headline, derived only from what the backend sent. */
export function hopLabel(hop: AuthorizationJourneyHop): string {
  if (hop.type === 'COMMUNICATION') {
    const method = hop.method ? methodLabel(hop.method) : 'Customer Contact';
    return hop.decision ? `${method} — ${decisionLabel(hop.decision)}` : method;
  }
  return hop.action ? actionLabel(hop.action) : 'Journey Entry';
}

/**
 * The movement a hop represents, as endpoint labels.
 *
 * An ownership hop reads owner -> owner. A customer leg reads waiting -> waiting
 * and carries NO owner endpoints, because that path cannot move ownership — which
 * is exactly why the customer never becomes a node in the ownership chain (OD-R5).
 */
export interface HopMovement {
  kind: 'OWNERSHIP' | 'WAITING';
  fromLabel: string | null;
  toLabel: string;
}

export function hopMovement(hop: AuthorizationJourneyHop): HopMovement | null {
  if (hop.toOwner) {
    return {
      kind: 'OWNERSHIP',
      // Null on the genesis hop: the case had no prior owner. Rendering
      // "Engineer" here would fabricate a predecessor Stage 2 deliberately
      // refused to persist.
      fromLabel: hop.fromOwner ? CURRENT_OWNER_LABELS[hop.fromOwner] : null,
      toLabel: CURRENT_OWNER_LABELS[hop.toOwner],
    };
  }
  if (hop.toWaitingFor) {
    return {
      kind: 'WAITING',
      fromLabel: hop.fromWaitingFor ? WAITING_FOR_LABELS[hop.fromWaitingFor] : null,
      toLabel: WAITING_FOR_LABELS[hop.toWaitingFor],
    };
  }
  return null;
}

/** True for the case-genesis hop — no prior owner existed. */
export function isGenesisHop(hop: AuthorizationJourneyHop): boolean {
  return hop.fromOwner === null && hop.toOwner !== null;
}

/**
 * Plain-language explanation of a failed self-verification, for the §16 state.
 *
 * Each one states what could not be reconciled. None of them suggests a repair,
 * offers a likely value, or implies the journey can be shown anyway.
 */
export function integrityMessage(journey: AuthorizationJourney): string {
  switch (journey.integrity.integrity) {
    case 'NO_TRANSITIONS_RECORDED':
      return 'No journey transitions have been recorded for this authorization, so its route cannot be shown.';
    case 'OWNER_MISMATCH':
      return 'The recorded journey ends with a different owner than the authorization currently has, so it cannot be shown as a verified route.';
    case 'WAITING_MISMATCH':
      return 'The recorded journey ends with a different waiting state than the authorization currently has, so it cannot be shown as a verified route.';
    case 'OWNER_AND_WAITING_MISMATCH':
      return 'The recorded journey ends with a different owner and waiting state than the authorization currently has, so it cannot be shown as a verified route.';
    default:
      return 'The recorded journey could not be verified against this authorization’s current state.';
  }
}
